import { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import IndoorMap from "../components/IndoorMap/IndoorMap";
import ShipmentStatus from "../components/ShipmentStatus";
import ActiveShipmentMetrics from "../components/ActiveShipmentMetrics";
import RemovedLGVStatus from "../components/RemovedLGVStatus";
import {
  setLocalStorageItem,
  apiHeadersFormatter,
  getLocalStorageItem,
  expireAuthToken,
  reloadApp
} from "../util/helper/helperFunctions";
import WarehouseModal from "../components/Modal/WarehouseModal/WarehouseModal";
import CustomizePopup from "../components/Modal/CustomizePopup/CustomizePopup";
import LayoutPopup from "../components/Modal/LayoutModal/LayoutPopup";
import { LegendsModal } from "../components/Modal";
import { plantDetailsAPI } from "../util/network/apiUrl";
import TimeMachineNoDataModal from "../components/Modal/TimeMachineNoData/TimeMachineNoData";
import NonLgvModal from "../components/Modal/NonLgvModal/NonLgvModal";
import AlertMessage from "../components/Reusables/AlertMessage/AlertMessage";
import DraggableComponent from "../components/DragableComponent/DraggableComponent";
import BatteryStationObjectPopup from "../components/Modal/BatteryStationObjectPopup/BatteryStationObjectPopup";
import { getApi } from "../util/network/getApi";
import {
  fetchFromLocalPlantsJSON,
  dashboardLegends,
  SOCKET_CHANNELS
} from "../util/helper/constants";
import { AppContext } from "../Context/Context";
import "../assets/css/Dashboard/dashboard.css";
import { setNonLGVsData } from "../store/inventorySlice";
import { normalizeData } from "../components/FilterDrawer/utils";
import {
  setActiveShipmentMetrics,
  setFleetCount,
  setFleetStatus
} from "../store/dashboardSlice";
import { setNonLGVInventoryData } from "../store/plantSlice";
import { useSocketDataEffect } from "../hooks/useSocketData";
import { setNavQualityHeatmapIsEnabled } from "../store/navHeatmapSlice";
import { setTimeMachineModeIsEnabled, setTimeMachineState } from "../store/oldTimeMachineSlice";
import { setTimeMachineV2ModeIsEnabled } from "../store/newTimeMachineSlice";

const Dashboard = () => {
  const { plantCode } = useParams();
  const currentLocation = useLocation();
  const dispatch = useDispatch();
  const {
    connectionStatus,
    removedLgvViewIsOpen,
    setRemovedLgvViewIsOpen,
    warehouseModelIsOpen,
    setPlantConfig,
    selectedMarkerData,
    selectedMarkerType,
    isLegendsModalOpen,
    customizePopup,
    layoutPopup,
    isMarkerPopupOpen,
    setLocationCode,
    isNoHeatMapDataModalOpen,
    isBlockRackModalOpen,
    alertObject,
    warehouseData,
    setWarehouseData,
    shiftPerformanceData,
    setShiftPerformanceData,
    socketConnection,
    isDefaultLayoutSelected,
    setIsDefaultLayoutSelected,
    setTimeMachineTimeFrames
  } = useContext(AppContext);

  const { data: nonLgvData, isModalOpen: isNonLgvModalOpen } = useSelector(
    (state) => state.plant.nonLGVInventory
  );
  const selectedBlockRackLocationData = useSelector(
    (state) => state.plant.selectedBlockRackLocationData
  );
  const [dashSwitches, setDashSwitches] = useState({
    productionLines: true,
    removedLgv: true,
    dockDoors: true,
    lgvObjects: true,
    destinationLines: true,
    dockDoorMode: false,
    batteryStation: true
  });

  useEffect(() => {
    if (localStorage.getItem("appSettings")) {
      const prevSwitchState = JSON.parse(localStorage.getItem("appSettings"));
      setDashSwitches((prev) => ({
        ...prev,
        ...prevSwitchState
      }));
    }
  }, []);

  /*resetting modes on coming back to dash page
  if user clicks on logo */
  useEffect(() => {
    //Old & new Time machine modes
    dispatch(setTimeMachineModeIsEnabled(false));
    dispatch(setTimeMachineV2ModeIsEnabled(false));
    dispatch(setTimeMachineState(false));
    setTimeMachineTimeFrames({});
    //navQ heat modes
    dispatch(setNavQualityHeatmapIsEnabled(false));
  }, []);

  const handleSettingsChange = (newSettings) => {
    localStorage.setItem("appSettings", JSON.stringify(newSettings));
    localStorage.setItem("dockMode", JSON.stringify(newSettings.dockDoorMode));
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        !connectionStatus &&
        currentLocation.pathname.split("/")[1] === plantCode
      ) {
        console.info("tab changed, reloading the page if socket disconnected");
        reloadApp();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [connectionStatus, plantCode, currentLocation.pathname]);

  useEffect(() => {
    const auth = getLocalStorageItem("auth");
    if (auth && plantCode && fetchFromLocalPlantsJSON) {
      const getLocalPlantDetails = async (auth, plantCode) => {
        const response = await getApi(
          `${plantDetailsAPI}/${plantCode}.json`,
          apiHeadersFormatter(auth.accessToken)
        );

        if (response?.status === 200 && response?.data) {
          setPlantConfig(response.data);
        } else if (
          response?.status === 401 ||
          response?.error === "Authentication Error!"
        ) {
          expireAuthToken();
        }
      };
      getLocalPlantDetails(auth, plantCode);
    }
  }, [plantCode]);

  //SHIFT PERFORMANCE
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.SHIFT,
    onData: setShiftPerformanceData
  });

  // WAREHOUSE CAPACITY
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.WAREHOUSE_CAPACITY,
    onData: setWarehouseData
  });

  // NON-LGV
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.NON_LGV,
    onData: (data) => {
      dispatch(setNonLGVInventoryData(data));
      dispatch(setNonLGVsData(normalizeData(data, "nonLGV")));
    }
  });

  // ACTIVE SHIPMENT METRICS
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.SHIPMENT,
    onData: (data) => dispatch(setActiveShipmentMetrics(data))
  });

  // FLEET COUNT
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.LGV_FLEET_COUNT,
    onData: (data) => {
      dispatch(setFleetCount(data.fleetData));
    },
    validate: (data) => (data?.fleetData ? true : false)
  });

  // FLEET STATUS
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.LGV_FLEET_STATUS,
    onData: (data) => dispatch(setFleetStatus(data.lgvStatus)),
    validate: (data) => (data?.lgvStatus ? true : false)
  });

  useEffect(() => {
    if (localStorage.getItem("appSettings")) {
      let prevSwitchState = getLocalStorageItem("appSettings");
      if (prevSwitchState.removedLgv) {
        setRemovedLgvViewIsOpen(true);
      }
      setLocalStorageItem("appSettings", prevSwitchState);
    }
  }, []);

  useEffect(() => {
    if (getLocalStorageItem("layout-order")) {
      setIsDefaultLayoutSelected(false);
    }
  }, []);

  let featureList = [
    {
      key: 1,
      component: <ShipmentStatus />
    },
    {
      key: 2,
      component: <IndoorMap socket={socketConnection} />
    },
    {
      key: 3,
      component: <ActiveShipmentMetrics />
    },
    {
      key: 4,
      component: removedLgvViewIsOpen ? <RemovedLGVStatus scrollable /> : null
    }
  ];

  const RenderFeatureListAsDraggables = useMemo(() => {
    if (!isDefaultLayoutSelected) {
      let order = getLocalStorageItem("layout-order");
      if (order) {
        featureList.sort((a, b) => {
          return order.indexOf(a.key) - order.indexOf(b.key);
        });
      }
    }

    if (featureList) {
      return (
        <DraggableComponent
          id="dashboard-table-id"
          draggableItems={featureList}
        />
      );
    }
  }, [socketConnection, removedLgvViewIsOpen, isDefaultLayoutSelected]);

  return (
    <>
      <div>
        <>
          {RenderFeatureListAsDraggables}
          {layoutPopup ? <LayoutPopup /> : null}
          {customizePopup ? (
            <CustomizePopup
              dashSwitches={dashSwitches}
              setDashSwitches={setDashSwitches}
              onSettingsChange={handleSettingsChange}
            />
          ) : null}
          {warehouseModelIsOpen ? (
            <WarehouseModal
              warehouseData={warehouseData}
              shiftData={shiftPerformanceData}
            />
          ) : null}
          {isLegendsModalOpen ? <LegendsModal data={dashboardLegends} /> : null}
          {socketConnection && isNonLgvModalOpen && nonLgvData ? (
            <NonLgvModal />
          ) : null}
          {selectedMarkerData &&
          selectedMarkerType === "batteryStationObj" &&
          isMarkerPopupOpen ? (
            <BatteryStationObjectPopup stationData={selectedMarkerData} />
          ) : null}
          {isNoHeatMapDataModalOpen ? <TimeMachineNoDataModal /> : null}
        </>
      </div>
      {alertObject.type.length !== 0 ? (
        <AlertMessage
          heading={alertObject.payload.heading}
          description={alertObject.payload.description}
          type={alertObject.type}
        />
      ) : null}
    </>
  );
};

export default Dashboard;
